/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Col, Modal, Row } from 'antd';
import { DateTime } from 'luxon';
import React, { useCallback, useRef } from 'react';
import { createFileName, useScreenshot } from 'use-react-screenshot';

import StatementLogo from '@portal/assets/svg/logos/statement_logo.svg?react';
import {
  StatementProofType,
  TransactionFlow,
  TransactionType,
} from '@portal/models/enumerators';

import { TransactionsActions } from '@portal/actions';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import { InvestmentApplications } from '@portal/models/module';
import { translate } from '@portal/services/i18n';
import { useDispatch } from 'react-redux';

import FormattedBilletStatement from '@portal/components/FormattedBilletStatement/FormattedBilletStatement';
import PaymentStatusIcon from '@portal/components/PaymentStatusIcon/PaymentStatusIcon';
import { cashback, customStyles, payment } from '@portal/config/app.json';
import { VITE_APPLICATION_NAME } from '@portal/config/env';
import { TransactionStatus } from '@portal/enum/approvalsType';
import {
  formatPixKey,
  isCpf,
  maskHiddenCpfOrCnpj,
} from '@portal/services/masks';
import { getEnumDescription } from '@portal/utils/enum';
import { moneyFormat } from '@portal/utils/format';
import * as Sentry from '@sentry/react';

interface IProps {
  transaction?: bank.Transaction | null;
  tradingNotes?: InvestmentApplications | null;
  onClose: () => void;
  username?: string;
  visible: boolean;
  title: string;
  statementProofType?: StatementProofType;
  hideEmail?: boolean;
}

const StatementProofModal: React.FC<IProps> = ({
  transaction,
  onClose,
  username,
  visible,
  title,
  statementProofType,
  tradingNotes,
  hideEmail,
}: IProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const [, takeScreenShot] = useScreenshot({
    type: 'image/jpeg',
    quality: 1.0,
  });

  const download = useCallback(
    (image) => {
      const a = document.createElement('a');
      a.href = image;

      if (transaction) {
        a.download = createFileName(
          'jpg',
          transaction.investmentInfo?.id ?? transaction.id
        );
        return a.click();
      }

      if (tradingNotes && tradingNotes.id) {
        a.download = createFileName('jpg', tradingNotes?.id);
        return a.click();
      }

      //@ts-ignore
      if (transaction?.billetInfo?.receiptFormatted) {
        a.download = createFileName('jpg', `billet-${new Date().getTime()}`);
        return a.click();
      }

      a.download = createFileName('jpg', 'statement-proof');
      return a.click();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transaction, tradingNotes, transaction?.billetInfo?.receiptFormatted]
  );

  const downloadScreenshot = () => {
    takeScreenShot(ref.current)
      .then(download)
      .catch((err) => {
        Sentry.captureException(err, {
          extra: { context: 'StatementProofModal.downloadScreenshot' },
        });
      });
    onClose();
  };

  const getStatusIcon = () => {
    if (!transaction) {
      return null;
    }

    if (
      transaction.status === TransactionStatus.SCHEDULE ||
      (transaction.transactionScheduleAt && !transaction.transactionAt)
    ) {
      return <PaymentStatusIcon status="scheduled" size={32} />;
    }

    if (
      transaction.status === TransactionStatus.EXECUTED &&
      !transaction.transactionScheduleAt
    ) {
      return <PaymentStatusIcon status="completed" size={32} />;
    }

    return null;
  };

  const statusIcon = getStatusIcon();

  const exportByEmail = () => {
    dispatch(
      TransactionsActions.exportTransactionVoucher(transaction?.id || '')
    );
    onClose();
  };

  const getDestinyName = () => {
    if (transaction) {
      switch (transaction.type) {
        case TransactionType.BILLET:
          return (
            transaction.shared?.destiny.userName ||
            transaction.billetInfo?.beneficiary?.name
          );

        case TransactionType.P2P:
          return transaction.p2pInfo?.payeeName;

        case TransactionType.TED:
          return (
            transaction.shared?.destiny.userName || transaction.tedInfo?.name
          );

        case TransactionType.CASHBACK:
          return transaction.cashbackInfo?.payeeName;

        case TransactionType.PIX:
          if (
            transaction.pixInfo &&
            transaction.flow === TransactionFlow.CASH_OUT
          ) {
            return (
              transaction.pixInfo?.name ?? transaction.shared?.destiny.userName
            );
          } else {
            return username ?? '';
          }

        default:
          if (transaction.flow === TransactionFlow.CASH_IN) {
            return username ?? '';
          } else {
            return '';
          }
      }
    }
  };

  const getOriginName = () => {
    if (transaction) {
      switch (transaction.type) {
        case TransactionType.P2P:
          return transaction.p2pInfo?.payerName;

        case TransactionType.CASHBACK:
          return transaction.cashbackInfo?.payerName;

        case TransactionType.PIX:
          if (
            transaction.pixInfo &&
            transaction.flow === TransactionFlow.CASH_IN
          ) {
            return transaction.pixInfo.name;
          } else {
            return username ?? '';
          }

        default:
          if (transaction.flow === TransactionFlow.CASH_OUT) {
            return username ?? '';
          } else {
            if (transaction.shared) return transaction.shared?.origin.userName;
            return '';
          }
      }
    }
  };

  const getBankNameDestiny = () => {
    if (transaction) {
      switch (transaction.type) {
        case TransactionType.P2P:
          return VITE_APPLICATION_NAME;

        case TransactionType.PIX:
          return transaction?.shared?.destiny.bankName;

        default:
          if (
            transaction.shared &&
            transaction.shared.destiny &&
            transaction.shared.destiny.bankName
          ) {
            return transaction.shared.destiny.bankName;
          } else {
            return '';
          }
      }
    }
  };

  const getBankNameOrigin = () => {
    if (transaction) {
      switch (transaction.type) {
        case TransactionType.P2P:
          return VITE_APPLICATION_NAME;

        case TransactionType.PIX:
          return transaction?.shared?.origin.bankName;

        default:
          if (
            transaction.shared &&
            transaction.shared.origin &&
            transaction.shared.origin.bankName
          ) {
            return transaction.shared.origin.bankName;
          } else {
            return '';
          }
      }
    }
  };

  const originNameResult = getOriginName();

  const renderTransactionDetails = () => {
    if (!transaction || !username) return null;

    return (
      <div className="users-transactions-details">
        {transaction?.billetInfo?.receiptFormatted ? (
          <Row className="users-transactions-details__billet">
            <Col span={12}>
              <FormattedBilletStatement
                billet={transaction?.billetInfo.receiptFormatted}
              />
            </Col>
          </Row>
        ) : (
          <>
            <div className="users-transactions-details__section">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <div className="users-transactions-details__field">
                    {transaction?.type && !transaction.isReversed ? (
                      <>
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TYPE'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {getEnumDescription(
                            transaction.type,
                            'TRANSACTION_TYPE'
                          )}
                        </p>
                      </>
                    ) : null}
                    {transaction?.type === TransactionType.PIX &&
                    transaction.flow === TransactionFlow.CASH_IN &&
                    transaction?.isReversed ? (
                      <>
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TYPE'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PIX_REVERSAL'
                          )}
                        </p>
                      </>
                    ) : null}
                    {transaction?.transactionScheduleAt &&
                    transaction.type === TransactionType.PIX ? (
                      <>
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TYPE'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.SCHEDULED_PIX_TRANSACTION'
                          )}
                        </p>
                      </>
                    ) : null}
                    {transaction?.transactionScheduleAt &&
                    transaction.type === TransactionType.TED ? (
                      <>
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TYPE'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.SCHEDULED_TED_TRANSACTION'
                          )}
                        </p>
                      </>
                    ) : null}
                    {transaction?.transactionScheduleAt &&
                    transaction.type === TransactionType.BILLET ? (
                      <>
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TYPE'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.SCHEDULED_BILLET_TRANSACTION'
                          )}
                        </p>
                      </>
                    ) : null}
                  </div>
                </Col>

                {transaction?.amount ? (
                  <Col xs={24} sm={12}>
                    <div className="users-transactions-details__field">
                      <h2 className="users-transactions-details__field__title">
                        {translate(
                          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.AMOUNT'
                        )}
                      </h2>
                      <p className="users-transactions-details__field__text">
                        {cashback.sideAccount
                          ? `${transaction?.amount} ${translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.POINTS'
                            )}`
                          : null}
                        {!cashback.sideAccount &&
                          moneyFormat(transaction?.amount)}
                      </p>
                    </div>
                  </Col>
                ) : null}
              </Row>
            </div>

            <div className="users-transactions-details__section">
              <Row gutter={[16, 16]}>
                {transaction?.transactionScheduleAt ? (
                  <Col xs={24} sm={12}>
                    <div className="users-transactions-details__field">
                      <h2 className="users-transactions-details__field__title">
                        {translate(
                          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.SCHEDULED_DATE'
                        )}
                      </h2>
                      <p className="users-transactions-details__field__text">
                        {DateTime.fromISO(
                          transaction.transactionScheduleAt
                        ).toLocaleString(DateTime.DATE_SHORT)}
                      </p>
                    </div>
                  </Col>
                ) : null}

                {transaction?.transactionAt ? (
                  <Col xs={24} sm={12}>
                    <div className="users-transactions-details__field">
                      <h2 className="users-transactions-details__field__title">
                        {translate(
                          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.CREATED_AT'
                        )}
                      </h2>
                      <p className="users-transactions-details__field__text">
                        {DateTime.fromISO(
                          transaction?.transactionAt
                        ).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                      </p>
                    </div>
                  </Col>
                ) : null}

                {transaction?.transactionReversalAt &&
                transaction.type == TransactionType.P2P &&
                (transaction.status == TransactionStatus.REVERSAL ||
                  transaction.status == TransactionStatus.CANCELLED) ? (
                  <Col xs={24} sm={12}>
                    <div className="users-transactions-details__field">
                      <h2 className="users-transactions-details__field__title">
                        {translate(
                          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.REVERSAL_DATE'
                        )}
                      </h2>
                      <p className="users-transactions-details__field__text">
                        {DateTime.fromISO(
                          transaction.transactionReversalAt
                        ).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                      </p>
                    </div>
                  </Col>
                ) : null}

                {transaction?.billetInfo?.dueDate ? (
                  <Col xs={24} sm={12}>
                    <div className="users-transactions-details__field">
                      <h2 className="users-transactions-details__field__title">
                        {translate(
                          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.DUE_DATE'
                        )}
                      </h2>
                      <p className="users-transactions-details__field__text">
                        {DateTime.fromISO(
                          transaction?.billetInfo?.dueDate
                        ).toLocaleString(DateTime.DATE_SHORT)}
                      </p>
                    </div>
                  </Col>
                ) : null}
              </Row>
            </div>

            {transaction?.billetInfo?.interest ||
            transaction?.billetInfo?.fine ||
            (transaction?.type === TransactionType.BILLET &&
              payment.showDiscountField) ? (
              <div className="users-transactions-details__section">
                <Row gutter={[16, 16]}>
                  {transaction?.billetInfo?.interest ? (
                    <Col xs={24} sm={12}>
                      <div className="users-transactions-details__field">
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.INTEREST'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {moneyFormat(transaction.billetInfo.interest)}
                        </p>
                      </div>
                    </Col>
                  ) : null}

                  {transaction?.billetInfo?.fine ? (
                    <Col xs={24} sm={12}>
                      <div className="users-transactions-details__field">
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.FINE'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {moneyFormat(transaction.billetInfo.fine)}
                        </p>
                      </div>
                    </Col>
                  ) : null}

                  {transaction?.type === TransactionType.BILLET &&
                  payment.showDiscountField ? (
                    <Col xs={24} sm={12}>
                      <div className="users-transactions-details__field">
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.DISCOUNT'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {moneyFormat(transaction?.billetInfo?.discount)}
                        </p>
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </div>
            ) : null}

            <div className="users-transactions-details__line" />

            <div className="users-transactions-details__section">
              <Row gutter={[16, 16]}>
                {transaction?.shared && transaction?.shared.origin ? (
                  <Col xs={24} md={12}>
                    <div className="users-transactions-details__field">
                      <h2 className="users-transactions-details__field__title">
                        {translate(
                          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.TITLE'
                        )}
                      </h2>
                      {originNameResult ? (
                        <p className="users-transactions-details__field__text">
                          <span>
                            {translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.NAME'
                            )}
                          </span>
                          <strong>{originNameResult}</strong>
                        </p>
                      ) : null}

                      {transaction?.shared.origin.userTaxIdentifier ? (
                        <p className="users-transactions-details__field__text">
                          <span>
                            {transaction?.type === TransactionType.PIX &&
                            transaction?.flow === TransactionFlow.CASH_IN
                              ? translate(
                                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.TAX_IDENTIFIER.BOTH'
                                )
                              : translate(
                                  `COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.TAX_IDENTIFIER.${
                                    isCpf(
                                      transaction?.shared.origin
                                        .userTaxIdentifier ?? ''
                                    )
                                      ? 'PERSON'
                                      : 'COMPANY'
                                  }`
                                )}
                          </span>
                          <strong>
                            {maskHiddenCpfOrCnpj(
                              transaction?.shared.origin.userTaxIdentifier ?? ''
                            )}
                          </strong>
                        </p>
                      ) : null}

                      {getBankNameOrigin() !== '' && (
                        <p className="users-transactions-details__field__text">
                          <span>
                            {translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYER.BANK'
                            )}
                          </span>
                          <strong>{getBankNameOrigin()}</strong>
                        </p>
                      )}
                    </div>
                  </Col>
                ) : null}

                {transaction?.shared && transaction?.shared.destiny ? (
                  <Col xs={24} md={12}>
                    <div className="users-transactions-details__field">
                      <h2 className="users-transactions-details__field__title">
                        {translate(
                          'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.TITLE'
                        )}
                      </h2>
                      {!!getDestinyName() && (
                        <p className="users-transactions-details__field__text">
                          <span>
                            {translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.NAME'
                            )}
                          </span>
                          <strong>{getDestinyName()}</strong>
                        </p>
                      )}
                      {transaction?.shared.destiny.userTaxIdentifier ? (
                        <p className="users-transactions-details__field__text">
                          <span>
                            {(transaction?.type === TransactionType.PIX ||
                              transaction?.type === TransactionType.BILLET) &&
                            transaction?.flow === TransactionFlow.CASH_OUT
                              ? translate(
                                  'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.TAX_IDENTIFIER.BOTH'
                                )
                              : translate(
                                  `COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.TAX_IDENTIFIER.${
                                    isCpf(
                                      transaction?.shared.destiny
                                        .userTaxIdentifier ?? ''
                                    )
                                      ? 'PERSON'
                                      : 'COMPANY'
                                  }`
                                )}
                          </span>
                          <strong>
                            {maskHiddenCpfOrCnpj(
                              (transaction?.shared.destiny.userTaxIdentifier ||
                                transaction?.billetInfo?.beneficiary
                                  ?.taxIdentifier) ??
                                ''
                            )}
                          </strong>
                        </p>
                      ) : null}
                      {getBankNameDestiny() && (
                        <p className="users-transactions-details__field__text">
                          <span>
                            {translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.BANK'
                            )}
                          </span>
                          <strong>{getBankNameDestiny()}</strong>
                        </p>
                      )}
                      {transaction?.shared.destiny &&
                      transaction?.shared.destiny?.userBankBranch ? (
                        <p className="users-transactions-details__field__text">
                          <span>
                            {translate(
                              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.BRANCH'
                            )}
                          </span>
                          <strong>
                            {transaction?.shared.destiny.userBankBranch.padStart(
                              4,
                              '0'
                            )}
                          </strong>
                        </p>
                      ) : null}
                    </div>
                  </Col>
                ) : null}
              </Row>
            </div>

            <div className="users-transactions-details__line" />

            {transaction?.pixInfo?.endToEndId ||
            transaction?.pixInfo?.key ||
            transaction?.bankId ||
            (transaction?.billetInfo &&
              transaction?.type === TransactionType.BILLET) ? (
              <div className="users-transactions-details__section">
                <Row gutter={[16, 16]}>
                  {transaction?.pixInfo && transaction.pixInfo.endToEndId ? (
                    <Col xs={24} sm={12}>
                      <div className="users-transactions-details__field">
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PIX_IDENTIFIER'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {transaction.pixInfo.endToEndId}
                        </p>
                      </div>
                    </Col>
                  ) : null}

                  {transaction?.pixInfo && transaction.pixInfo.key ? (
                    <Col xs={24} sm={12}>
                      <div className="users-transactions-details__field">
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.PAYEE.KEY'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {formatPixKey(transaction.pixInfo.key)}
                        </p>
                      </div>
                    </Col>
                  ) : null}

                  {transaction?.bankId ? (
                    <Col xs={24} sm={12}>
                      <div className="users-transactions-details__field">
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.TRANSACTION_ID'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {transaction.bankId}
                        </p>
                      </div>
                    </Col>
                  ) : null}

                  {transaction?.billetInfo &&
                  transaction?.type === TransactionType.BILLET ? (
                    <Col xs={24}>
                      <div className="users-transactions-details__field">
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.BARCODE'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text">
                          {transaction.billetInfo.typeableLine}
                        </p>
                      </div>
                    </Col>
                  ) : null}

                  {transaction?.description ? (
                    <Col xs={24} sm={12}>
                      <div className="users-transactions-details__field">
                        <h2 className="users-transactions-details__field__title">
                          {translate(
                            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.LABELS.DESCRIPTION'
                          )}
                        </h2>
                        <p className="users-transactions-details__field__text users-transactions-details__field__text--block">
                          {transaction.description}
                        </p>
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const getStatementProofType = () => {
    if (!statementProofType) {
      return;
    }
    switch (statementProofType) {
      case StatementProofType.INVESTMENT:
        if (!transaction) return;
        return renderTransactionDetails();

      case StatementProofType.TRADING_NOTES:
        if (!tradingNotes) return;

        return renderTransactionDetails();

      default:
        if (!transaction || !username) return;
        return renderTransactionDetails();
    }
  };

  return (
    <Modal
      open={visible}
      className={`${
        customStyles.compactReceipt && 'compact-statement'
      } statement-modal-capsule`}
      footer={null}
      onCancel={onClose}
      width={!transaction?.billetInfo?.receiptFormatted ? 920 : undefined}
    >
      <div ref={ref}>
        {!transaction?.billetInfo?.receiptFormatted && (
          <div className="statement-proof-modal">
            <div className="statement-proof-modal__header-title">
              {statusIcon ? (
                <div className="statement-proof-modal__header-title__icon">
                  {statusIcon}
                </div>
              ) : null}
              <h2 className="statement-proof-modal__header-title__text">
                {title}
              </h2>
            </div>
            <div className="statement-proof-modal__logo">
              <StatementLogo width={220} height={80} />
            </div>
            {getStatementProofType()}

            {customStyles.statementFooter ? (
              <div className="statement-proof-modal__footer">
                <p className="statement-proof-modal__text">
                  {translate(
                    'COMPONENTS.USERS_TRANSACTIONS.DETAILS.FOOTER.TEXT_1'
                  )}
                </p>
                <p className="statement-proof-modal__text">
                  {translate(
                    'COMPONENTS.USERS_TRANSACTIONS.DETAILS.FOOTER.TEXT_2'
                  )}
                </p>
              </div>
            ) : null}
          </div>
        )}

        {transaction?.billetInfo?.receiptFormatted ? (
          <FormattedBilletStatement
            billet={transaction?.billetInfo?.receiptFormatted}
          />
        ) : null}
      </div>
      <div
        id="buttons-div"
        className={
          transaction?.billetInfo?.receiptFormatted ? 'buttons-div--single' : ''
        }
      >
        <AdvancedButton
          text={translate(
            'COMPONENTS.USERS_TRANSACTIONS.DETAILS.BUTTONS.DOWNLOAD'
          )}
          onClick={downloadScreenshot}
        />
        {!transaction?.billetInfo?.receiptFormatted && !hideEmail && (
          <AdvancedButton
            text={translate(
              'COMPONENTS.USERS_TRANSACTIONS.DETAILS.BUTTONS.SHARE_BY_EMAIL'
            )}
            onClick={exportByEmail}
          />
        )}
      </div>
    </Modal>
  );
};

export default StatementProofModal;
